import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Button,
  ColorsV2,
  Divider,
  Flex,
  Header,
  Select,
  Skeleton,
  Text,
  getToken,
  spaces,
  useThemeColors,
} from '@electricjs/arc';
import {
  EmployeeLeaveStatusEnum,
  EmployeeStatusEnum,
} from '@electricjs/core_entity-client';
import { useEmployeeDirectory } from '@/hooks/useEmployeeDirectory';
import { useGetBulkEmployeeLeavesQuery } from '@/redux/slices/employeeApiSlice';
import ExternalLink from 'common/ExternalLink';
import { getDisplayFullName } from 'common/utils/getFullName';
import { useTaskDescription } from '@/hooks/useTaskDescription';
import { isEmployeeOnLeave } from '@/helpers/isEmployeeOnLeave';
import EmployeeOnLeaveIcon from '@/components/People/EmployeeOnLeaveIcon';
import type { Request } from '@/types/requests';
import type { Employee, EmployeeDirectoryListing } from '@/types/employees';
import type { ChampionOption } from '@/pages/Organization/Settings/ChampSelectorV2';

const { space_s, space_m, space_l } = spaces;

type Props = {
  tasks: Request[];
  employee: Employee;
  organizationId: string;
  onNext: (reassignToEmployeeId: string | null) => void;
  onBack: () => void;
  isLastStep: boolean;
};

type AssigneeOption = ChampionOption;

const TableContainer = styled.div`
  border: 1px solid ${getToken(ColorsV2.BORDER)};
  border-radius: 0.8rem;
  width: 100%;
  overflow: hidden;
  background: ${getToken(ColorsV2.BACKGROUND_ALT)};
`;

const TableRow = styled.div<{ $stripe?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  padding: ${space_m};
  box-sizing: border-box;
  background: ${({ $stripe }) =>
    $stripe ? getToken(ColorsV2.BACKGROUND) : 'transparent'};
`;

const HeaderRow = styled(TableRow)`
  background: ${getToken(ColorsV2.BACKGROUND_ALT)};
  padding: ${space_s} ${space_m};
  border-bottom: 1px solid ${getToken(ColorsV2.BORDER)};
`;

const TaskNameCell = ({ task }: { task: Request }) => {
  const { description, isLoading } = useTaskDescription({
    id: task.id,
    data: task.data,
  });

  if (isLoading) {
    return <Skeleton width="20rem" height="1.6rem" />;
  }

  return <Text>{description ?? task.id}</Text>;
};

const ReassignTasks = ({
  tasks,
  employee,
  organizationId,
  onNext,
  onBack,
  isLastStep,
}: Props) => {
  const { t } = useTranslation('common');
  const { ithEmployeeLeave } = useFlags();
  const [textLightColor] = useThemeColors([ColorsV2.TEXT_LIGHT]);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );

  const { data: employees, isFetching: isFetchingEmployees } =
    useEmployeeDirectory({
      organizationId,
      status: EmployeeStatusEnum.Active,
    });

  const candidateEmployees = useMemo(
    () =>
      sortBy(
        (employees ?? []).filter(
          (e): e is EmployeeDirectoryListing =>
            !!e.email && e.id !== employee.id
        ),
        e => getDisplayFullName(e).toLowerCase()
      ),
    [employees, employee.id]
  );

  const employeeIds = useMemo(
    () => candidateEmployees.map(e => e.id),
    [candidateEmployees]
  );

  const {
    data: employeeLeavesData,
    isLoading: isLoadingLeaves,
    isError: isErrorLeaves,
  } = useGetBulkEmployeeLeavesQuery(
    ithEmployeeLeave && employeeIds.length
      ? {
          organizationId,
          employeeIds,
          // InProgress powers the on-leave icon; Scheduled is also needed so
          // it can show the upcoming-leave indicator.
          statuses: [
            EmployeeLeaveStatusEnum.InProgress,
            EmployeeLeaveStatusEnum.Scheduled,
          ],
        }
      : skipToken
  );

  // Without this, a failed bulk fetch silently renders as "no one is on
  // leave" with no signal that the data is missing rather than empty.
  useEffect(() => {
    if (isErrorLeaves) {
      window?.DD_RUM?.addError('Failed to fetch bulk employee leave status', {
        location: 'ReassignTasks',
      });
    }
  }, [isErrorLeaves]);

  const assigneeOptions = useMemo<AssigneeOption[]>(
    () =>
      candidateEmployees.map(e => ({
        employeeId: e.id,
        employee: e,
        label: getDisplayFullName(e),
        employeeLeaves: employeeLeavesData?.[e.id] ?? [],
      })),
    [candidateEmployees, employeeLeavesData]
  );

  const isOptionDisabled = (option: AssigneeOption) =>
    isEmployeeOnLeave(option.employeeLeaves);

  const selectedOption =
    assigneeOptions.find(o => o.employeeId === selectedEmployeeId) ?? null;

  const handleNext = () => {
    onNext(selectedEmployeeId);
  };

  const employeeName = getDisplayFullName(employee);

  return (
    <>
      <Header
        title={t('offboardings.reassignTasks.title')}
        subtitle={t('offboardings.reassignTasks.subtitle', { employeeName })}
      />

      <TableContainer>
        <HeaderRow>
          <Text variant="label-large">
            {t('offboardings.reassignTasks.columnName')}
          </Text>
        </HeaderRow>

        {tasks.map((task, i) => (
          <div key={task.id}>
            {i > 0 && <Divider />}
            <TableRow $stripe={i % 2 === 0}>
              <TaskNameCell task={task} />
            </TableRow>
          </div>
        ))}
      </TableContainer>

      <Flex vertical gap={space_s} mt={space_l} maxWidth="45rem" width="100%">
        <Text variant="label-large">
          {t('offboardings.reassignTasks.selectLabel')}
        </Text>
        <Select
          id="reassign-tasks-assignee-select"
          name="reassign-tasks-assignee-select"
          placeholder={t('offboardings.reassignTasks.selectPlaceholder')}
          options={assigneeOptions}
          value={selectedOption}
          onChange={option => {
            const opt = option as AssigneeOption | null;
            setSelectedEmployeeId(opt?.employeeId ?? null);
          }}
          isSearchable
          isClearable={false}
          isLoading={isFetchingEmployees}
          // Until the leave check itself resolves, employeeLeaves defaults to
          // [] for every option (see isOptionDisabled below), which would let
          // an on-leave employee be picked in that window. Disable the whole
          // select rather than let that race through.
          disabled={isLoadingLeaves}
          getOptionValue={(o: AssigneeOption) => o.employeeId}
          isOptionDisabled={isOptionDisabled}
          formatOptionLabel={(o: AssigneeOption) => (
            <Flex alignItems="center">
              <EmployeeOnLeaveIcon
                employeeId={o.employeeId}
                employeeStatus={o.employee?.status}
                leaves={o.employeeLeaves}
              />
              {o.label}
            </Flex>
          )}
        />
      </Flex>

      <Flex alignItems="center" gap="0.4rem" mt={space_s} flexWrap="wrap">
        <Text variant="body-2" color={textLightColor}>
          {t('offboardings.reassignTasks.tasksPrompt')}
        </Text>
        <ExternalLink
          id="reassign-tasks-tasks-link"
          href={`/tasks?tasks=all&assignedTo=${employee.id}`}
          variant="body-2"
          showIcon={false}>
          {t('offboardings.reassignTasks.tasksLinkLabel')}
        </ExternalLink>
      </Flex>

      <Flex mt="9rem" hAlignContent="right" gap="3.4rem" alignSelf="flex-end">
        <Button id="reassign-tasks-back-button" variant="text" onClick={onBack}>
          {t('offboardings.reassignTasks.backButton')}
        </Button>
        <Button
          id="reassign-tasks-next-button"
          disabled={!selectedEmployeeId}
          onClick={handleNext}>
          {t(
            isLastStep
              ? 'offboardings.reassignTasks.nextButtonLastStep'
              : 'offboardings.reassignTasks.nextButtonNextStep'
          )}
        </Button>
      </Flex>
    </>
  );
};

export default ReassignTasks;
